import React, { useState, useEffect, useMemo } from "react";
import "../../css/CRUDModal.css";
import ButtonTriangle from "../../assets/Icons/Button Triangle.png";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useForm, Controller } from "react-hook-form";
import { Box, CircularProgress } from "@mui/material";
import CustomAlert from "../../helpers/CustomAlert";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import LeftCircle from "../../assets/Icons/Left circle B.png";
import RightCircle from "../../assets/Icons/Right circle A.png";
import FilterIcon from "../../assets/Icons/Orange Filter.png";
import CrossIcon from "../../assets/Icons/cross_svg.svg";
import Dropdown from "../Dropdown";

const statusOptions = [
  { value: "Active", label: "Active" },
  { value: "In-Active", label: "In-Active" },
  { value: "Block", label: "Block" },
];

const BASE_PROFILE_OPTIONS = [
  { value: "ADMK", label: "Admin Maker" },
  { value: "CUSTOMER", label: "Customer" },
  { value: "FRANCHISE", label: "Franchise" },
  { value: "HUB", label: "Hub" },
  { value: "OPERATION", label: "Operation" },
  { value: "SUB_LMBP", label: "Sub-LMBP" },
];

const PROFILE_LABEL_MAP = {
  ADMK: "Admin Maker",
  CUSTOMER: "Customer",
  FRANCHISE: "Franchise",
  HUB: "Hub",
  OPERATION: "Operation",
  SUB_LMBP: "Sub-LMBP",
  "SUB-LMBP": "Sub-LMBP",
  RETAILER: "Retailer",
};

const RoleFilterModal = ({
  show,
  handleClose,
  setRoleData,
  setPageDetails,
  setNoData,
  pageSize,
  currentPage,
  filterData,
  setFilterData,
  handleReset,
  roleDataList = [],
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { isSubmitting, errors },
  } = useForm({
    defaultValues: {
      ...filterData,
      userProfile: filterData.userProfile || null,
      roleName: filterData.roleName || "",
      status: filterData.status || [],
      fromDate: filterData.fromDate || "",
      toDate: filterData.toDate || "",
    },
    mode: "onChange",
  });

  // Watch all form values to check if a filter has a value
  const watchedValues = watch();

  useEffect(() => {
    if (show) {
      reset({
        userProfile: filterData?.userProfile || null,
        roleName: filterData?.roleName || "",
        status: filterData?.status || [],
        fromDate: filterData?.fromDate || "",
        toDate: filterData?.toDate || "",
      });
    }
  }, [show, filterData, reset]);

  const combinedProfileOptions = useMemo(() => {
    const profileMap = new Map();

    BASE_PROFILE_OPTIONS.forEach((opt) => {
      profileMap.set(opt.value.toUpperCase(), opt);
    });

    if (Array.isArray(roleDataList)) {
      roleDataList.forEach((item) => {
        const val = item?.userProfile;
        if (val && typeof val === "string" && val.trim() !== "") {
          const upperVal = val.trim().toUpperCase();
          if (!profileMap.has(upperVal)) {
            const formattedLabel =
              PROFILE_LABEL_MAP[upperVal] ||
              val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
            profileMap.set(upperVal, {
              value: val.trim(),
              label: formattedLabel,
            });
          }
        }
      });
    }

    const list = Array.from(profileMap.values());
    list.sort((a, b) =>
      a.label.trim().localeCompare(b.label.trim(), undefined, {
        sensitivity: "base",
      })
    );
    return list;
  }, [roleDataList]);

  const currentProfileValue =
    (typeof watchedValues?.userProfile === "object"
      ? watchedValues?.userProfile?.value
      : watchedValues?.userProfile) ||
    (typeof filterData?.userProfile === "object"
      ? filterData?.userProfile?.value
      : filterData?.userProfile) ||
    "";

  const profileDropdownOptions = useMemo(() => {
    if (currentProfileValue) {
      return [
        { label: "✕ Clear User Profile Selection", value: "" },
        ...combinedProfileOptions,
      ];
    }
    return combinedProfileOptions;
  }, [combinedProfileOptions, currentProfileValue]);

  // Function to check if a tab has an active filter value
  const hasActiveFilter = (tabName) => {
    switch (tabName) {
      case "User Profile":
        return Boolean(
          (filterData?.userProfile?.value && filterData.userProfile.value !== "") ||
          (typeof filterData?.userProfile === "string" && filterData.userProfile.trim() !== "")
        );
      case "Role Name":
        return Boolean(filterData?.roleName && filterData.roleName.trim() !== "");
      case "Status":
        return Boolean(filterData?.status && filterData.status.length > 0);
      case "Created Date":
        return Boolean(
          (filterData?.fromDate && filterData.fromDate !== "") ||
          (filterData?.toDate && filterData.toDate !== "")
        );
      default:
        return false;
    }
  };

  const [selectedTab, setSelectedTab] = useState("User Profile");
  const [loading, setLoading] = useState(false);
  const loggedInUserName = sessionStorage.getItem("username");

  const handleTabChange = (e, tabName) => {
    e.preventDefault();
    setSelectedTab(tabName);
  };

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };

  const onSubmit = (data) => {
    setFilterData(data);
    fetchRoleData(currentPage, data);
  };

  const fetchRoleData = async (page, formData) => {
    setLoading(true);
    try {
      const selectedProfile =
        formData.userProfile?.value ||
        (typeof formData.userProfile === "string" ? formData.userProfile : "");

      const requestBody = {
        username: loggedInUserName,
        userProfile: selectedProfile,
        roleName: formData.roleName || "",
        status:
          formData.status.length > 0
            ? formData.status
            : ["Active", "Block", "In-Active"],
        fromDate: formatDate(formData.fromDate),
        toDate: formatDate(formData.toDate),
      };

      const response = await axiosPostApi(
        `${apiList.FETCH_ROLE_DATA}?pageSize=${pageSize}&pageNo=${page}`,
        requestBody,
      );

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setPageDetails(response?.data?.pageDetails);
        let modifiedData = modifyStructure(response?.data?.data);
        if (selectedProfile && modifiedData) {
          modifiedData = modifiedData.filter(
            (item) =>
              (item.userProfile || "").toUpperCase() ===
              selectedProfile.toUpperCase()
          );
        }
        setRoleData(modifiedData);
        setNoData(modifiedData?.length === 0);
        handleClose();
      } else {
        CustomAlert({
          title: "Failed",
          text:
            response?.data?.message ||
            "There was an issue processing the request.",
          icon: "error",
          confirmButtonText: "Retry",
        });
        setRoleData([]);
        setNoData(true);
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoading(false);
    }
  };

  const modifyStructure = (response) => {
    return response?.map((item, index) => ({
      ...item,
      id: index + 1,
    }));
  };

  const onReset = () => {
    reset({
      userProfile: null,
      roleName: "",
      status: [],
      fromDate: "",
      toDate: "",
    });
    setFilterData({
      userProfile: null,
      roleName: "",
      status: [],
      fromDate: "",
      toDate: "",
    });
    handleReset();
  };

  return (
    <>
      {show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1060,
          }}
        >
          <div className="modal-dialog" role="document">
            <div
              className="modal-content"
              style={{
                width: "40vw",
                borderRadius: "24px",
                padding: "24px",
                backgroundColor: "#FFF",
                border: "1px solid rgba(0, 0, 0, 0.175)",
              }}
            >
              <img
                src={LeftCircle}
                alt="leftImg"
                className="absolute left-0 bottom-0 w-[110px] h-[105px]"
              />
              <img
                src={RightCircle}
                alt="leftImg"
                className="absolute right-0 top-0 w-[110px] h-[105px]"
              />
              <div
                className="modal-header"
                style={{
                  padding: "1rem 1rem",
                  borderBottom: "1px solid #dee2e6",
                  justifyContent: "space-between",
                  display: "flex",
                }}
              >
                <div className="flex items-center gap-2">
                  <img src={FilterIcon} alt="filter icon" className="w-6 h-6" />
                  <h5 className="modal-title main-title">Filter</h5>
                </div>
                <img
                  src={CrossIcon}
                  alt="filter icon"
                  className="w-[30px] h-[30px] cursor-pointer z-20"
                  onClick={handleClose}
                />
              </div>
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <div
                  className="scoped-modal modal-body"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <div className="tab-container">
                    <ul className="tab-list">
                      {[
                        "User Profile",
                        "Role Name",
                        "Status",
                        "Created Date",
                      ].map((tab) => (
                        <div className="tab-wrapper" key={tab}>
                          <li
                            className={
                              selectedTab === tab
                                ? "tab-item selected"
                                : "tab-item"
                            }
                            style={
                              hasActiveFilter(tab) && selectedTab !== tab
                                ? {
                                    outline: "2px solid #FF6B35",
                                    outlineOffset: "2px",
                                  }
                                : {}
                            }
                          >
                            <button
                              className={`tab-button ${
                                selectedTab === tab ? "active" : ""
                              }`}
                              onClick={(e) => handleTabChange(e, tab)}
                            >
                              {tab}
                            </button>
                          </li>
                          {selectedTab === tab && (
                            <img
                              src={ButtonTriangle}
                              alt="tri"
                              className="tab-triangle"
                            />
                          )}
                        </div>
                      ))}
                    </ul>

                    <div className="tab-content !bg-[linear-gradient(to_bottom,#FFE8D4_0%_100%,transparent_25%_100%)]">
                      {selectedTab === "User Profile" && (
                        <Controller
                          name="userProfile"
                          control={control}
                          render={({ field }) => (
                            <div className="dateField">
                              <label className="dateLabel">User Profile</label>
                              <div className="dateInputWrapper">
                                <Dropdown
                                  placeholder="Select User Profile"
                                  options={profileDropdownOptions}
                                  value={
                                    field.value?.value ||
                                    (typeof field.value === "string"
                                      ? field.value
                                      : "") ||
                                    filterData?.userProfile?.value ||
                                    (typeof filterData?.userProfile === "string"
                                      ? filterData.userProfile
                                      : "") ||
                                    ""
                                  }
                                  labelKey="label"
                                  valueKey="value"
                                  showSearch={true}
                                  selectClassName="!h-[38px] !rounded-[6px]"
                                  onChange={(item) => {
                                    const selected =
                                      item && item.value
                                        ? { value: item.value, label: item.label }
                                        : null;
                                    field.onChange(selected);
                                    setFilterData((prev) => ({
                                      ...prev,
                                      userProfile: selected,
                                    }));
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        />
                      )}

                      {selectedTab === "Role Name" && (
                        <Controller
                          name="roleName"
                          control={control}
                          render={({ field }) => (
                            <div className="dateField">
                              <label className="dateLabel">Role Name</label>
                              <input
                                {...field}
                                type="text"
                                placeholder="Enter Role Name"
                                className="dateInput"
                                maxLength={50}
                                onChange={(e) => {
                                  field.onChange(e);
                                  setFilterData((prev) => ({
                                    ...prev,
                                    roleName: e.target.value,
                                  }));
                                }}
                              />
                            </div>
                          )}
                        />
                      )}

                      {selectedTab === "Status" && (
                        <div className="dateField">
                          <label
                            className="dateLabel"
                            style={{
                              marginBottom: "0.5rem",
                              fontWeight: "600",
                              fontSize: "20px",
                            }}
                          >
                            Status
                          </label>
                          <div
                            className="dateInputWrapper"
                            style={{
                              display: "flex",
                              gap: "1rem",
                              flexWrap: "wrap",
                              padding: "0.75rem",
                              backgroundColor: "#ffdab8",
                              border: "1px solid #ced4da",
                              borderRadius: "0.5rem",
                            }}
                          >
                            {statusOptions.map((status) => (
                              <label
                                key={status.value}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.5rem",
                                  padding: "0.5rem 0.75rem",
                                  borderRadius: "0.375rem",
                                  cursor: "pointer",
                                  fontSize: "0.9rem",
                                  fontWeight: "500",
                                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={(filterData.status || []).includes(
                                    status.value,
                                  )}
                                  onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    const newStatus = isChecked
                                      ? [
                                          ...(filterData.status || []),
                                          status.value,
                                        ]
                                      : (filterData.status || []).filter(
                                          (v) => v !== status.value,
                                        );
                                    setValue("status", newStatus);
                                    setFilterData((prev) => ({
                                      ...prev,
                                      status: newStatus,
                                    }));
                                  }}
                                  style={{ cursor: "pointer" }}
                                />
                                {status.label}
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedTab === "Created Date" && (
                        <div
                          className="dateGrid"
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "1rem",
                            alignItems: "flex-start",
                          }}
                        >
                          <Controller
                            name="fromDate"
                            control={control}
                            rules={{
                              validate: {
                                validDate: (value) =>
                                  !value ||
                                  !isNaN(Date.parse(value)) ||
                                  "Invalid From Date",
                                notFuture: (value) =>
                                  !value ||
                                  new Date(value) <= new Date() ||
                                  "From Date cannot be in the future",
                                beforeToDate: (value) =>
                                  !value ||
                                  !filterData.toDate ||
                                  new Date(value) <=
                                    new Date(filterData.toDate) ||
                                  "From Date must be before To Date",
                                requiredIfToDate: (value, filterData) =>
                                  !filterData.toDate ||
                                  !!value ||
                                  "From Date is required when To Date is selected",
                              },
                            }}
                            render={({ field }) => (
                              <div className="dateField" style={{ flex: 1 }}>
                                <label className="dateLabel">From Date</label>
                                <div className="dateInputWrapper">
                                  <input
                                    {...field}
                                    type="date"
                                    className="dateInput"
                                    max={getTodayDate()}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      setFilterData((prev) => ({
                                        ...prev,
                                        fromDate: e.target.value,
                                      }));
                                    }}
                                  />
                                  {errors.fromDate && (
                                    <div className="error-text">
                                      {errors.fromDate.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                          <Controller
                            name="toDate"
                            control={control}
                            rules={{
                              validate: {
                                validDate: (value) =>
                                  !value ||
                                  !isNaN(Date.parse(value)) ||
                                  "Invalid To Date",
                                notFuture: (value) =>
                                  !value ||
                                  new Date(value) <= new Date() ||
                                  "To Date cannot be in the future",
                                afterFromDate: (value) =>
                                  !value ||
                                  !filterData.fromDate ||
                                  new Date(value) >=
                                    new Date(filterData.fromDate) ||
                                  "To Date must be after From Date",
                                requiredIfFromDate: (value, filterData) =>
                                  !filterData.fromDate ||
                                  !!value ||
                                  "To Date is required when From Date is selected",
                              },
                            }}
                            render={({ field }) => (
                              <div className="dateField" style={{ flex: 1 }}>
                                <label className="dateLabel">To Date</label>
                                <div className="dateInputWrapper">
                                  <input
                                    {...field}
                                    type="date"
                                    className="dateInput"
                                    max={getTodayDate()}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      setFilterData((prev) => ({
                                        ...prev,
                                        toDate: e.target.value,
                                      }));
                                    }}
                                  />
                                  {errors.toDate && (
                                    <div className="error-text">
                                      {errors.toDate.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className="scoped-modal"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "12px",
                    paddingBottom: "0px",
                    gap: "16px",
                    borderTop: "1px solid #dee2e6",
                  }}
                >
                  <div className="buttonContainer">
                    <button
                      type="button"
                      className="btn btn-back w-[133px] !rounded-lg"
                      onClick={onReset}
                    >
                      Reset
                    </button>
                  </div>
                  <div className="buttonContainer">
                    <button
                      type="submit"
                      className="btn btn-submit w-[133px] !rounded-lg"
                      disabled={isSubmitting}
                    >
                      {loading ? (
                        <CircularProgress
                          style={{ width: "20px", height: "20px" }}
                        />
                      ) : (
                        "Apply"
                      )}
                    </button>
                  </div>
                </div>
              </Box>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RoleFilterModal;
